// src/components/organizer/OrganizerRevenueWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Chip,
  Divider,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  EventSeat as RegistrationIcon,
  Store as MerchandiseIcon,
  AccountBalance as SponsorIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';

const OrganizerRevenueWidget = ({ data }) => {
  const [tabValue, setTabValue] = useState(0);

  if (!data) return null;

  const revenueStreams = [
    {
      name: 'Inscripciones',
      amount: data.registrations || 0,
      percentage: ((data.registrations / data.total) * 100).toFixed(1),
      growth: '+18%',
      color: 'primary',
      icon: <RegistrationIcon />
    },
    {
      name: 'Patrocinios',
      amount: data.sponsorships || 0,
      percentage: ((data.sponsorships / data.total) * 100).toFixed(1),
      growth: '+25%',
      color: 'success',
      icon: <SponsorIcon />
    },
    {
      name: 'Merchandising',
      amount: data.merchandise || 0,
      percentage: ((data.merchandise / data.total) * 100).toFixed(1),
      growth: '+12%',
      color: 'info',
      icon: <MerchandiseIcon />
    }
  ];

  const recentTransactions = [
    {
      id: 1,
      type: 'registration',
      description: 'Inscripción - Juan Pérez (Maratón)',
      amount: 150,
      date: '2024-12-09 14:30',
      status: 'completed',
      event: 'Maratón Ciudad'
    },
    {
      id: 2,
      type: 'sponsorship',
      description: 'Patrocinio - SportTech Solutions',
      amount: 5000,
      date: '2024-12-09 10:15',
      status: 'completed',
      event: 'Copa Juvenil'
    },
    {
      id: 3,
      type: 'merchandise',
      description: 'Venta Camiseta Oficial',
      amount: 35,
      date: '2024-12-09 09:20',
      status: 'completed',
      event: 'Triatlón Regional'
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getTransactionIcon = (type) => {
    const icons = {
      registration: <RegistrationIcon />,
      sponsorship: <SponsorIcon />,
      merchandise: <MerchandiseIcon />,
      payment: <PaymentIcon />
    };
    return icons[type] || <ReceiptIcon />;
  };

  const getTransactionColor = (type) => {
    const colors = {
      registration: 'primary',
      sponsorship: 'success',
      merchandise: 'info',
      payment: 'secondary'
    };
    return colors[type] || 'default';
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="💰 Mis Ingresos"
        subheader={`Total: ${formatCurrency(data.total)}`}
        action={
          <Box display="flex" gap={1}>
            <Tooltip title="Exportar reporte">
              <IconButton size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Chip 
              label={`+${data.growth}%`} 
              color="success" 
              size="small"
              icon={<TrendingUpIcon />}
            />
          </Box>
        }
      />
      
      <CardContent>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Resumen" />
          <Tab label="Transacciones" />
        </Tabs>

        {/* Tab 1: Resumen de ingresos */}
        {tabValue === 0 && (
          <Box>
            {/* Total destacado */}
            <Paper 
              sx={{ 
                p: 2, 
                mb: 3, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textAlign: 'center'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {formatCurrency(data.total)}
              </Typography>
              <Typography variant="body1">
                Ingresos de Mis Eventos
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={1}>
                <TrendingUpIcon />
                <Typography variant="body2">
                  +{data.growth}% vs mes anterior
                </Typography>
              </Box>
            </Paper>

            {/* Fuentes de ingresos */}
            <Typography variant="subtitle2" gutterBottom>
              💳 Fuentes de Ingresos
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {revenueStreams.map((stream, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Paper sx={{ p: 2, height: '100%', border: 1, borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Avatar sx={{ bgcolor: `${stream.color}.main`, width: 32, height: 32 }}>
                        {React.cloneElement(stream.icon, { fontSize: 'small' })}
                      </Avatar>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {stream.name}
                      </Typography>
                    </Box>
                    
                    <Typography variant="h6" fontWeight="bold" color={`${stream.color}.main`}>
                      {formatCurrency(stream.amount)}
                    </Typography>
                    
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(stream.percentage)}
                        color={stream.color}
                        sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
                      />
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {stream.percentage}% del total
                        </Typography>
                        <Chip
                          label={stream.growth}
                          color="success"
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 16 }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Objetivos */}
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                🎯 Objetivos del Mes
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Meta de Ingresos
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formatCurrency(data.monthlyGoal || 50000)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={((data.thisMonth || 35000) / (data.monthlyGoal || 50000)) * 100}
                      color="primary"
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Progreso Actual
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {Math.round(((data.thisMonth || 35000) / (data.monthlyGoal || 50000)) * 100)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(data.thisMonth || 35000)} logrados
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* Tab 2: Transacciones recientes */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              📋 Transacciones Recientes
            </Typography>
            
            <List dense>
              {recentTransactions.map((transaction) => (
                <ListItem key={transaction.id} divider>
                  <ListItemIcon>
                    <Avatar 
                      sx={{ 
                        bgcolor: `${getTransactionColor(transaction.type)}.main`,
                        width: 32,
                        height: 32
                      }}
                    >
                      {React.cloneElement(getTransactionIcon(transaction.type), { fontSize: 'small' })}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          {transaction.description}
                        </Typography>
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {transaction.event} • {new Date(transaction.date).toLocaleString('es-ES')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Resumen del día */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="caption" color="success.dark">
                💼 Hoy: {recentTransactions.length} transacciones • {' '}
                {formatCurrency(recentTransactions.reduce((sum, t) => sum + t.amount, 0))} en ingresos
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
export default OrganizerRevenueWidget;